import React, { useEffect, useState } from 'react';
import { Form, Schema, Stack, Col, Grid, Row, IconButton, Divider, FormInstance, Modal, Input, Button} from 'rsuite';
import PageContent from '@/components/PageContent';
import { BaseErrorModel, BaseResponseModel, LatLng } from '@/models/core.model';
import { useNavigate } from 'react-router-dom';
import PageNextIcon from '@rsuite/icons/PageNext';
import PagePreviousIcon from '@rsuite/icons/PagePrevious';
import ErrorMessage from '@/components/ErrorMessage';
import { AxiosError } from 'axios';
import { toastSuccessMessage } from '@/utils/toasterMessage';
import Textarea from '@/components/Textarea';
import { GoogleMap, Marker, Polygon, useJsApiLoader } from '@react-google-maps/api';
import { getBoundingBoxCenter } from '@/utils/map';
import { hk_lat_lng } from '@/constants/map';
import { insertCityMultipleDistrict } from '@/services/city-district.service';
import FetchingSelect from '@/components/FetchingSelect';
import { getCitiesLookup, getCity } from '@/services/city.service';
import { City } from '@/models/city.model';

interface CityMultipleDistrictFormProps{
  city?: City,
};
const emptyModel = {
  cityId: undefined,
  json: ''
};
const CityMultipleDistrictForm: React.FC<CityMultipleDistrictFormProps> = ({city}) => {
  const [formValue, setFormValue] = useState<{cityId?: number, json?: string}>(emptyModel);
  const [formError, setFormError] = useState({});
  const [errorModel, setErrorModel] = useState<BaseErrorModel|undefined>(undefined);
  const [loading, setLoading] = useState(false);
  const [polygonsPaths, setPolygonsPaths] = useState<DistrictPolygon[]>([]);
  const [mapCenter, setMapCenter] = useState<LatLng|undefined>(undefined);
  const [selectedPolygon, setSelectedPolygon] = useState<DistrictPolygon|undefined>(undefined);
  const [open, setOpen] = useState<boolean>(false);

 const { isLoaded } = useJsApiLoader({
    id: 'google-map-script',
    googleMapsApiKey: process.env.REACT_APP_MAP_API_KEY!,
  });
  const nav = useNavigate();
  const formRef = React.useRef<FormInstance<Record<string, any>>>(null);

  const CityDistrictSchema = Schema.Model({
    cityId: Schema.Types.NumberType().isRequired('This field is required.'),
    json: Schema.Types.StringType().isRequired('This field is required.')
      .addRule(val => mapGeoJsonToModel(val).length > 0, 'Invalid districts JSON structure.')
  });

  useEffect(() => {
    setFormValue({...emptyModel, cityId: city?.id});
    if(city){
      setMapCenter({lat: city?.lat, lng: city?.lon});
    }
  },[city]);

  interface GeoJsonFeatureCollection {
    type: "FeatureCollection";
    features: GeoJsonFeature[];
  }

  interface GeoJsonFeature {
    type: "Feature";
    geometry: {
      type: "Polygon";
      coordinates: number[][][];
    };
    properties: {
      id: number;
      name: string;
      centerLat: number;
      centerLng: number;
    };
  }

  interface DistrictPolygon {
    id: number;
    name: string;
    polygon: LatLng[];
    center: LatLng;
  }

  function mapGeoJsonToModel(json: string): DistrictPolygon[] {
    try {
      const geoJson = JSON.parse(json) as GeoJsonFeatureCollection;
      return geoJson.features
        .filter(f => f.geometry.type === "Polygon")
        .map(f => ({
          id: f.properties.id,
          name: f.properties.name,
          polygon: f.geometry.coordinates[0].map(([lng, lat]) => ({ lat, lng })),
          center: {lat: f.properties.centerLat, lng: f.properties.centerLng}
        }));
    } catch (error) {
      return [];
    }      
  }

  const updatePolygon = (json: string) => {
    const polygons = mapGeoJsonToModel(json);
    if(polygons && polygons.length > 0) {
      setPolygonsPaths(polygons);
      setMapCenter(getBoundingBoxCenter(polygons.map(p => p.center)));
      return true;
    } else { 
      setPolygonsPaths([]);
      if(city){
        setMapCenter({lat: city?.lat, lng: city?.lon});
      }      
      return false;
    }
  };

  const handleSubmit = async () => {
    // console.log(formValue);
    if (formRef.current && !formRef.current.check()) {
      console.log(formError);
      return;
    }
    setErrorModel(undefined);
    setLoading(true);
    try {
      const casted = {
        cityId: formValue.cityId, 
        districts: polygonsPaths.map(p => ({name: p.name, coordinates: p.polygon}))
      };
      const result: BaseResponseModel<null> = await insertCityMultipleDistrict(casted);
      if (result.success) {
        toastSuccessMessage('City District saved successfully.');
         nav(`/cities/${formValue.cityId}`, {state: {tab: 'city-districts'}});
      }
    } catch (err) {
      setErrorModel((err as AxiosError<BaseErrorModel>).response?.data);
    }finally{
      setLoading(false);
    }
  };
  
  const updateSelectedCity = async(cityId: number|undefined) => {
    if(cityId){
      getCity(cityId).then(result => {
        setMapCenter({lat: result.result?.lat, lng: result.result?.lon});
      });
      return;
    }
  };

  const handleFormChange = (value: Record<string, any>) => {
    setFormValue(prevFormValue => ({
      ...prevFormValue,
      ...value
    }));
  };

  const handleNameChange = (value: string) => {
    setSelectedPolygon(pre => pre ? ({ ...pre, name: value }) : pre);

    setPolygonsPaths(prev =>
      prev.map(p =>
        p.id === selectedPolygon?.id
          ? { ...p, name: value }
          : p
      )
    );

    const geoJson = JSON.parse(formValue.json ?? '') as GeoJsonFeatureCollection;
    geoJson.features = geoJson.features.map(f =>
      f.properties.id === selectedPolygon?.id
        ? { ...f, properties: {...f.properties, name: value} }
        : f
    );
    setFormValue(pre => ({...pre, json: JSON.stringify(geoJson)}));
  };

  const polygonStyle = {
    strokeColor: "#1E3A8A", // deep blue
    strokeOpacity: 0.9,
    strokeWeight: 2,
    fillColor: "#60A5FA", // soft blue
    fillOpacity: 0.75
  };

  const labelStyle = {
    color: "#fff",
    fontSize: "13px",
    fontWeight: "600"
  };
  return (
    <>
      <PageContent>
        <Form fluid ref={formRef}
          onCheck={setFormError}
          formValue={formValue}
          onChange={handleFormChange}
          model={CityDistrictSchema}
          disabled={loading}
        >
          <Grid w='100%' fluid>
            <Row>
              <Col span={{xs:24, xl:8}} >
                <Form.Group>
                  <Form.Label>City</Form.Label>
                  <Form.Control block
                    name= "cityId" 
                    accepter= {FetchingSelect} 
                    fetchData= {(text, page) => getCitiesLookup(text, undefined, page)}
                    disabled= {!!city} 
                    onChange={ val => updateSelectedCity(val)}
                  />
                </Form.Group>
                <Form.Group>
                  <Form.Label>Json Geometry</Form.Label>
                  <Form.Control 
                    name="json" 
                    value={formValue.json ?? ''} 
                    accepter={Textarea} 
                    style={{height: '650px'}}
                    onChange={json => updatePolygon(json)}
                  />
                </Form.Group>
              </Col>
              <Col span={{xs:24, xl:16}} style={{textAlign: 'center'}}>
                <Form.Group>
                  <Form.Label>Districts On Map (Click on districts to change their name)</Form.Label>
                    {isLoaded ? <GoogleMap
                      mapContainerStyle={{ width: '100%', height: '700px', borderRadius: 10 }}
                      center={mapCenter || hk_lat_lng}
                      zoom={12}
                    >
                      {polygonsPaths.map(p => <Polygon
                        key={p.id}
                        paths={p.polygon}
                        options={polygonStyle}
                        onClick={()=>{
                          setSelectedPolygon(p);
                          setOpen(true);
                        }}
                      />)}
                      {polygonsPaths.map(p => <Marker
                        key={p.id}
                        position={p.center}
                        label= {{...labelStyle, text: p.name}}
                        icon= {{
                          path: google.maps.SymbolPath.CIRCLE,
                          scale: 0 // hides the marker icon
                        }}

                      />)}
                    </GoogleMap> : '' }                
                </Form.Group>
              </Col>              
            </Row>
          </Grid>
        </Form>

        <Divider />
        {errorModel && <ErrorMessage model={errorModel} style={{ marginTop: 20, marginBottom:20 }}></ErrorMessage>}

        <Stack justifyContent="space-between">
          <IconButton icon={<PagePreviousIcon />} onClick={() => nav(-1)}>
            Back
          </IconButton>
          <IconButton
            icon={<PageNextIcon />}
            placement="right"
            appearance="primary"
            onClick={handleSubmit}
            loading={loading}
          >
            Save
          </IconButton>
        </Stack>

        <Modal open={open} onClose={() => setOpen(false)}>
          <Modal.Body>
            <label>District Name</label>
            <Input
              placeholder="Type and press Add"
              value={selectedPolygon?.name}
              onChange={handleNameChange}
            />
          </Modal.Body>
          <Modal.Footer>
            <Button onClick={() => setOpen(false)} appearance="primary">
              Close
            </Button>
          </Modal.Footer>
        </Modal>

      </PageContent>
    </>
  );
};

export default CityMultipleDistrictForm;
